/** AquaMap (pre-2026) / gps-x GPX stores depth as negative feet in {@code ele}. */
export const NEGATIVE_ELE_FEET = "negative_ele_feet";

/** Newer AquaMap exports store depth as negative meters in {@code ele} (GPX spec). */
export const NEGATIVE_ELE_METERS = "negative_ele_meters";

/** Legacy GPX treats {@code ele} as elevation in meters. */
export const ELE_METERS = "ele_meters";

const AQUAMAP_CREATOR = /aqua\s*map/i;
const GPSX_CREATOR = /gps-x/i;

/**
 * AquaMap switched its GPX export from negative feet to spec-compliant
 * negative meters somewhere between Apr 2023 (confirmed feet) and Jul 2026
 * (confirmed meters). The files are otherwise identical, so tracks recorded
 * on or after this date are assumed to be meters. Refine when the exact
 * AquaMap release date is confirmed; the viewer offers a manual override.
 */
export const AQUAMAP_METERS_CUTOVER_MS = Date.parse("2026-01-01T00:00:00Z");

/** Matches GpxTrackParser.DEPTH_METERS_KEYWORD. gps-x writes this when ele is meters. */
export const DEPTH_METERS_KEYWORD = "gpsx:depth=meters";

const latestTrackTimeMs = (xmlDoc) => {
  const points = xmlDoc.getElementsByTagName("trkpt");
  // Track points are chronological; walk from the end to find a timestamp.
  for (let i = points.length - 1; i >= 0; i--) {
    const time = points[i].getElementsByTagName("time")[0];
    if (time?.textContent) {
      const parsed = Date.parse(time.textContent);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
};

export function detectEleDepthConvention(xmlDoc) {
  const gpx = xmlDoc?.documentElement;
  if (!gpx) {
    return ELE_METERS;
  }

  const creator = gpx.getAttribute("creator") || "";
  if (GPSX_CREATOR.test(creator)) {
    const keywords = gpx.getElementsByTagName("keywords")[0]?.textContent || "";
    return keywords.includes(DEPTH_METERS_KEYWORD)
      ? NEGATIVE_ELE_METERS
      : NEGATIVE_ELE_FEET;
  }
  if (AQUAMAP_CREATOR.test(creator)) {
    return latestTrackTimeMs(xmlDoc) >= AQUAMAP_METERS_CUTOVER_MS
      ? NEGATIVE_ELE_METERS
      : NEGATIVE_ELE_FEET;
  }

  const points = xmlDoc.getElementsByTagName("trkpt");
  let withEle = 0;
  let nonPositive = 0;
  const sampleSize = Math.min(points.length, 50);

  for (let i = 0; i < sampleSize; i++) {
    const ele = points[i].getElementsByTagName("ele")[0];
    if (ele?.textContent) {
      withEle++;
      if (Number(ele.textContent) <= 0) {
        nonPositive++;
      }
    }
  }

  if (withEle > 0 && nonPositive / withEle >= 0.8) {
    return NEGATIVE_ELE_FEET;
  }

  return ELE_METERS;
}

export function parseRawDepthFromPoint(point, convention) {
  const depthExt = point.getElementsByTagName("gpxtpx:depth");
  if (depthExt.length > 0) {
    return Number(depthExt[0]?.textContent || 0);
  }

  const eleNodes = point.getElementsByTagName("ele");
  if (eleNodes.length === 0) {
    return 0;
  }

  const ele = Number(eleNodes[0]?.textContent || 0);
  if (convention === NEGATIVE_ELE_FEET || convention === NEGATIVE_ELE_METERS) {
    return -ele;
  }

  return ele;
}

export function displayDepth(rawDepth, convention, metric, offset = 0) {
  let depth;
  if (convention === NEGATIVE_ELE_FEET) {
    depth = metric ? rawDepth * 0.3048 : rawDepth;
  } else {
    // NEGATIVE_ELE_METERS and ELE_METERS both carry raw meters
    depth = metric ? rawDepth : rawDepth * 3.28084;
  }

  return depth - Number(offset);
}

export function depthToEleText(displayedDepth, convention, metric) {
  if (convention === NEGATIVE_ELE_FEET) {
    const feet = metric ? displayedDepth / 0.3048 : displayedDepth;
    if (Math.abs(feet) < 0.05) {
      return "0.0";
    }
    return (-Math.abs(feet)).toFixed(1);
  }

  const meters = metric ? displayedDepth : displayedDepth * 0.3048;
  if (convention === NEGATIVE_ELE_METERS) {
    if (Math.abs(meters) < 0.05) {
      return "0.0";
    }
    return (-Math.abs(meters)).toFixed(1);
  }

  return Math.abs(meters).toFixed(1);
}
